<template>
  <Header navbarType="initial" v-if="showHeader" class="mb-0 pb-0" />
  <Alert
    v-if="showAlert"
    :message="alertMessage"
    show-help-btn
    alertType="alert-warning"
    closable
    @onClose="showAlert = false"
  />
  <!--//////////////////////////////////LOADING//////////////////////////////////-->
  <LoadingOverlay
    :loading="
      currentStep === RegistrationSteps.LOADING ||
      currentStep === RegistrationSteps.FINISHING_PROCESS
    "
  />
  <main
    class="home"
    ref="mainRef"
    :class="currentStep === RegistrationSteps.OFFER ? 'offer-bg' : ''"
    v-if="currentStep !== RegistrationSteps.LOADING"
  >
    <div
      class="main-body py-0"
      :class="currentStep === RegistrationSteps.FINISHING_PROCESS && 'px-0'"
    >
      <!--//////////////////////////////////CARD INTRO//////////////////////////////////-->
      <RegistrationCardIntro
        v-if="currentStep === RegistrationSteps.CARD_INTRO"
      />
      <!--//////////////////////////////////OFFER DISPLAY//////////////////////////////////-->
      <NewOfferDisplay
        v-if="currentStep === RegistrationSteps.OFFER_DISPLAY"
        :user-offer="userOffer"
        @switch-step="switchStep"
        @toggle-show-modal="() => (showModalInterestExp = true)"
      />

      <!--//////////////////////////////////TERMS & CONTRACT//////////////////////////////////-->
      <RegistrationTermsAndContract
        v-if="currentStep === RegistrationSteps.ACCEPT"
        :marketing-communications-checkbox="marketingCommunicationsCheckbox"
        :user-offer="userOffer"
        :full-name="fullName"
        :is-privacy-policy-enabled="props.privacyPolicy?.enabled"
        @set-doc-modal-content="setDocModalContent"
        @clearSignature="clearSignature"
        @on-change-marketing-communications="onChangeMarketingCommunications"
        @sign-contract="signContract"
        @switch-step="switchStep"
        @show-modal-interest-exp="() => (showModalInterestExp = true)"
      />
      <!--//////////////////////////////////REJECT OFFER//////////////////////////////////-->
      <RegistrationRejectOffer
        v-if="currentStep === RegistrationSteps.REJECT"
        @show-not-selected-reject-reason-alert="
          showNotSelectedRejectReasonAlert
        "
        @go-back="switchStep(RegistrationSteps.OFFER_DISPLAY)"
      />
      <!--//////////////////////////////////NOT APPLICABLE//////////////////////////////////-->
      <RegistrationNotApplicable
        v-if="currentStep === RegistrationSteps.NOT_APPLICABLE"
        :error-code="erroCode"
      />
      <!--//////////////////////////////////OFFER EXPIRED//////////////////////////////////-->
      <RegistrationOfferExpired
        v-if="currentStep === RegistrationSteps.OFFER_EXPIRED"
      />
      <!--//////////////////////////////////GENERIC ERROR//////////////////////////////////-->
      <RegistrationGenericError
        v-if="currentStep === RegistrationSteps.GENERIC_ERROR"
        :error-code="erroCode"
      />
    </div>
    <Modal :show="showDocsModal">
      <template #header>
        <div class="d-flex flex-row">
          <h5 class="text-black fw-bolder text-start" id="modal-title-header">
            {{ docModalTitle }}
          </h5>
          <span class="ms-auto me-0 right" @click="showDocsModal = false"
            ><i class="fa fa-times" style="font-size: larger !important"></i
          ></span>
        </div>
      </template>
      <template #body class="mt-2">
        <div
          v-if="docsModalContent === RegistrationSteps.ACCEPT"
          ref="termsRef"
          id="modal-body-terms"
          class="content p-2 overflow-auto c1c"
          style="scale: 0.8"
          v-html="termsContent"
        ></div>
        <div
          v-if="docsModalContent === RegistrationSteps.CONTRACT"
          ref="contractRef"
          id="modal-body-contract"
          class="content overflow-auto c1c"
          v-html="contractContent"
        ></div>
        <div
          v-if="docsModalContent === RegistrationSteps.PRIVACY_POLICY"
          ref="privacyPolicyRef"
          id="modal-body-privacy-policy"
          class="content overflow-auto c1c"
        >
          <iframe
            v-if="privacyPolicyUrl"
            :src="privacyPolicyUrl"
            style="width: 100%; min-height: 60vh; border: none; border-radius: 8px;"
            allowfullscreen
          ></iframe>
          <div v-else class="text-center py-4">
            {{ t('ERROR_SOMETHING_GOES_WRONG') }}
          </div>
        </div>
      </template>
    </Modal>

    <InterestExplainerModal
      :show="showModalInterestExp"
      :borrowed="Number(userOffer.approvedCreditLine)"
      :interest-total="userOffer.example?.interests"
      :minimum-payment="Number(userOffer.expectedMinimumPayment)"
      @close-modal="() => (showModalInterestExp = false)"
    />
  </main>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, PropType } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { AxiosError } from "axios";

import Header from "@/shared-components/consumer/Header.vue";
import Alert from "@/shared-components/common/Alert.vue";
import Modal from "@/apps/credit-line/components/Modal.vue";
import RegistrationCardIntro from "@/apps/credit-line/components/Registration/CardIntro.vue";
import RegistrationRejectOffer from "@/apps/credit-line/components/Registration/RejectOffer.vue";
import RegistrationTermsAndContract from "@/shared-components/consumer/T&C.vue";
import RegistrationNotApplicable from "@/apps/credit-line/components/Registration/NotApplicable.vue";
import RegistrationOfferExpired from "@/apps/credit-line/components/Registration/OfferExpired.vue";
import RegistrationGenericError from "@/apps/credit-line/components/Registration/GenericError.vue";
import NewOfferDisplay from "@/apps/credit-line/components/Registration/NewOfferDisplay.vue";
import InterestExplainerModal from "@/apps/credit-line/components/Registration/InterestExplainerModal.vue";
import LoadingOverlay from "@/shared-components/ui/core-app/LoadingOverlay.vue";

import { Service } from "@/apps/credit-line/services/network.service";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import { IOffer, IOffers } from "@/apps/credit-line/models/IOffer";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import DatadogLogger from "@/shared-helpers/datadog-logger.helper";
import { LogType } from "@/types/dataDogLogType";
import { IBaseResponse } from "@/apps/credit-line/models/IBaseResponse";
import { getErrorMessage } from "@/apps/credit-line/helpers/backCodesStrings.helper";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { openChatAfterSeconds } from "@/apps/merchant3/helpers/chatBotPopup";
import MerchantFlags from "@/apps/merchant3/types/MerchantFlags";
import { CL_COUNTRIES } from "@/apps/credit-line/types/cl_countries";
import { RegistrationSteps } from "@/apps/credit-line/types/registrationSteps";
import {
  buildContractAndTyc,
  gtagEventSetUserProps,
  registrationAnalyticsSteps,
} from "@/apps/credit-line/helpers/registrations.helper";
import { NumericConverter } from "@/shared-helpers/numeric-converter.helper";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";
import { SettingValue } from "@/shared-models/consumer/ISetting";
declare const Android: AndroidInterface;

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const CreditLineService = new Service();
const converter = new NumericConverter();

const props = defineProps({
  creditLineContract: {
    type: Object as PropType<SettingValue>,
    required: true,
  },
  termsAndConditions: {
    type: Object as PropType<SettingValue>,
    required: true,
  },
  privacyPolicy: {
    type: Object as PropType<SettingValue>,
    required: true,
  },
  hardOneFunnel: {
    type: Object as PropType<SettingValue>,
    required: true,
  },
  creditLineOfferFields: {
    type: Object as PropType<SettingValue>,
    required: true,
  },
});

// Refs
const mainRef = ref();
const termsRef = ref();
const contractRef = ref();
const currentStep = ref<RegistrationSteps>(RegistrationSteps.LOADING);
const showModalInterestExp = ref(false);
const erroCode = ref<number>();
const userOffer = ref({} as IOffer);
const alertMessage = ref<string>(t("SELECTANOPTION"));
const showAlert = ref(false);
const termsContent = ref<string>();
const fullName = ref<string>();
const contractContent = ref<string>();
const country = ref<CL_COUNTRIES>();
const docModalTitle = ref<string>();
const showDocsModal = ref(false);
const docsModalContent = ref<
    RegistrationSteps.ACCEPT | RegistrationSteps.CONTRACT | RegistrationSteps.PRIVACY_POLICY
>();
const marketingCommunicationsCheckbox = ref(true);
const digitalSignature = ref<string>("");

const useChatPopup = computed(
  () =>
    store.state.merchant?.merchant?.country === "ZA" &&
    store.state.merchant?.merchantFlags?.includes(
      MerchantFlags.USE_ADA_CHATBOT_AUTO_TRIGGER
    )
);

const showHeader = computed(
  () =>
    currentStep.value !== RegistrationSteps.CARD_INTRO &&
    currentStep.value !== RegistrationSteps.LOADING &&
    currentStep.value !== RegistrationSteps.OFFER_DISPLAY &&
    currentStep.value !== RegistrationSteps.ACCEPT
);

onMounted(() => {
  logAnalyticsEvent(
    AnalyticEvents.CreditLine.ViewOfferLoading,
    "Happy Path",
    "Preparing offer"
  );
  if (useChatPopup.value) {
    openChatAfterSeconds(300);
  }
  getOffer();
});

watch(currentStep, registrationAnalyticsSteps);

watch(showDocsModal, (value) => {
  document.body.style.overflow = value ? "hidden" : "auto";
});

function switchStep(selected: RegistrationSteps) {
  currentStep.value = selected;
  if (selected === RegistrationSteps.ACCEPT) {
    logAnalyticsEvent(
      AnalyticEvents.CreditLine.ClickOfferSelected,
      "Happy Path",
      "Offer details"
    );
  }
}

function setDocModalContent(
  step: RegistrationSteps.ACCEPT | RegistrationSteps.CONTRACT | RegistrationSteps.PRIVACY_POLICY
) {
  showDocsModal.value = true;
  docsModalContent.value = step;
  switch (step) {
    case RegistrationSteps.ACCEPT:
      docModalTitle.value = t("CL_CONTRACT.TYC_TITLE");
      logAnalyticsEvent(
        AnalyticEvents.CreditLine.ClickTyC,
        "Happy Path",
        "Click - T&C triggered"
      );
      break;
    case RegistrationSteps.CONTRACT:
      docModalTitle.value = t("CL_CONTRACT.CONTRACT_TITLE");
      logAnalyticsEvent(
        AnalyticEvents.CreditLine.ClickContract,
        "Happy Path",
        "Click - Contract triggered"
      );
      break;
    case RegistrationSteps.PRIVACY_POLICY:
      docModalTitle.value = t("PRIVACY_POLICY_TITLE");
      logAnalyticsEvent(
        AnalyticEvents.CreditLine.ClickPrivacyPolicy,
        "Happy Path",
        "Click - Privacy Policy triggered"
      );
      break;
  }
}

function logCreditlineError(response: IBaseResponse | IOffers) {
  const whitelistedCodes = [710, 725];
  if (response.message) {
    showAlert.value = true;
    alertMessage.value = getErrorMessage(
      response.code || 705,
      t,
      AuthenticationHelper.getDeviceTag()
    );
  }

  const errorMsg = `Credit Line Error: ${JSON.stringify(response)}`;
  if (!whitelistedCodes.includes(response.code || 0)) {
    DatadogLogger.Log(
      errorMsg,
      response as Record<string, unknown>,
      LogType.ERROR
    );
  }
}

function showNotSelectedRejectReasonAlert() {
  alertMessage.value = t("SELECTANOPTION");
  showAlert.value = true;
}

function acceptErrorCallback(code = 0) {
  if (code === 702) {
    currentStep.value = RegistrationSteps.OFFER_EXPIRED;
  } else if (code === 814 || code === 770) {
    Android.creditOfferAccepted();
  } else {
    currentStep.value = RegistrationSteps.GENERIC_ERROR;
  }
}

const offerFetchedAccepted = async () => {
  try {
    await CreditLineService.acceptOffer({
      deviceTag: AuthenticationHelper.getDeviceTag(),
      offerID: userOffer.value.offerID,
      currency: userOffer.value.currency,
      approvedCreditLine: userOffer.value.approvedCreditLine,
      paymentTerm: userOffer.value.paymentTerm,
      paymentTermUnit: userOffer.value.paymentTermUnit,
      annualPercentageRate: userOffer.value.annualPercentageRate,
      annualPercentageRateDisplay: userOffer.value.annualPercentageRateDisplay,
      hashValue: userOffer.value.hashValue,
      expiry: userOffer.value.expiry,
      decision: "accept",
      taxPercent: userOffer.value.taxPercent,
      cat: userOffer.value.cat ?? 0,
      makeModel: userOffer.value.makeModel,
      creditCardEligible: userOffer.value.creditCardEligible ?? false,
      digitalSignature: digitalSignature.value,
      marketingConsent: marketingCommunicationsCheckbox.value,
      annualEffective: userOffer.value.annualEffective ?? 0,
      monthlyNominalValue: userOffer.value.monthlyNominalValue ?? 0,
      idNumber: userOffer.value.idNumber ?? 0,
      weeklyTermLetters: converter.numberToWords(userOffer.value.paymentTerm),
      approvedCreditLimitLetters: converter.numberToWords(
        userOffer.value.approvedCreditLine
      ),
      availabilityFeeRate: userOffer.value.availabilityFeeRate ?? 0,
      insuranceFeeRate: userOffer.value.insuranceFeeRate ?? 0,
    });

    gtagEventSetUserProps();

    if (isAndroid()) {
      if (userOffer.value.creditCardEligible) {
        const isHardOneFunnelEnabled = props.hardOneFunnel?.enabled === true;

        if (isHardOneFunnelEnabled) {
          router.replace({ name: "CreditCardShippingAddressView" });
        } else {
          currentStep.value = RegistrationSteps.CARD_INTRO;
        }
      } else {
        Android.creditOfferAccepted();
      }
    }
  } catch (err) {
    const error = err as AxiosError;
    if (error.response) {
      logCreditlineError(error.response);
      acceptErrorCallback(error.response.data?.errorCode);
    }
  }
};

function signContract(signatureData: {
  isEmpty: boolean;
  data: string | undefined;
}) {
  if (signatureData.data) {
    digitalSignature.value = signatureData.data.replace(
      "data:image/png;base64,",
      ""
    );
  }

  if (!signatureData.isEmpty) {
    alertMessage.value = "";
    showAlert.value = false;
    logAnalyticsEvent(
      AnalyticEvents.CreditLine.ClickContractSigned,
      "Happy Path",
      "Contract signature"
    );
    currentStep.value = RegistrationSteps.FINISHING_PROCESS;
    offerFetchedAccepted();
  } else {
    alertMessage.value = t("PLEASE_SIGN");
    showAlert.value = true;
    window.scrollTo(0, 0);
  }
}

function offerSuccessCallback(offer: IOffer) {
  if (!offer) {
    currentStep.value = RegistrationSteps.NOT_APPLICABLE;
    return;
  }

  store.dispatch("creditLine/setOffer", offer);
  fullName.value = offer?.fullName || "";
  country.value = offer.country;
  try {
    const docService = buildContractAndTyc(offer, Android.getDeviceTag());
    termsContent.value = docService?.buildTyC(
      props.termsAndConditions.values?.mobileVersion?.template
    );
    contractContent.value = docService?.buildContract(
      props.creditLineContract.values?.mobileVersion?.template
    );
    // Privacy Policy is now loaded via iFrame only, no content build needed
  } catch (error) {
    termsContent.value = "";
    contractContent.value = "";
    // No privacyPolicyContent to clear
    console.log(error);
  }
  userOffer.value = offer;
  currentStep.value = RegistrationSteps.OFFER_DISPLAY;
}

async function getOffer() {
  if (store.state.creditLine.offer_unavailable === true) {
    currentStep.value = RegistrationSteps.NOT_APPLICABLE;
    return;
  }

  try {
    const deviceTag = AuthenticationHelper.getDeviceTag() as string;
    const response = await CreditLineService.getOffers({
      deviceTag,
      taxPercent: 0.16,
    });
    offerSuccessCallback(response.data.offer);
  } catch (err: unknown) {
    const error = err as AxiosError;
    if (error.response && error.response.data?.errorCode) {
      if ([721, 814].includes(error.response.data?.errorCode)) {
        Android.creditOfferAccepted();
      } else if ([722, 723].includes(error.response.data?.errorCode)) {
        currentStep.value = RegistrationSteps.GENERIC_ERROR;
      } else {
        currentStep.value = RegistrationSteps.NOT_APPLICABLE;
      }
      erroCode.value = error.response.data?.errorCode;
      logCreditlineError(error.response);
    }
  }

  Android.finishedLoading();
}

function clearSignature() {
  digitalSignature.value = "";
}

function onChangeMarketingCommunications(event: Event) {
  const target = event?.target as HTMLInputElement;
  marketingCommunicationsCheckbox.value = target.checked;
}

const privacyPolicyUrl = computed((): string => {
  if (props.privacyPolicy && props.privacyPolicy.enabled && props.privacyPolicy.values && props.privacyPolicy.values.url) {
    return props.privacyPolicy.values.url as string;
  }
  return "";
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

main {
  position: relative;
  & * {
    position: relative;
  }
}

#modal-title-header h5 {
  font-style: normal;
  font-family: Nunito;
  font-size: large;
}

#modal-btn-cta {
  border-radius: 10px;
  border: 1px solid var(--main-primary-500, #2c7040);
  background: var(--main-white, #fff);
  color: var(--main-primary-500, #2c7040);
  height: 44px;
  padding: 10px 24px;
  text-align: center;
  font-size: medium;
}

#exp-time {
  color: orange;
}

.offer-bg {
  background-color: #f6fbf9 !important;
  height: 90vh;
}

.light-text {
  color: $pj_color-cool-gray;
}

#modal-body-terms,
#modal-body-contract,
#modal-body-privacy-policy {
  max-height: 65vh !important;
}

.disabled {
  background-color: $pj-color-gray;
}

.offer-summary {
  display: flex;
  flex-direction: column;
  background: $pj-color-green-hover;
  padding: 10px;
  border-radius: 16px;
}

.loading-bg {
  background: #fff;
}

.gray-text {
  color: $pj-color-gray;
  font-weight: lighter;
}

.btn-disabled {
  background-color: $pj-color-gray;
  cursor: not-allowed;
}

.c1c {
  scale: 0.87 1 !important;
}

.cancel-btn {
  color: $pj-color-green;
  background-color: white;
  border-color: transparent;
}

.clear-btn {
  color: $pj-color-yellow-green;
}

label {
  cursor: pointer;
  color: $pj-color-green;
  font-weight: bold;
  font-size: medium;
}

.checkbox {
  box-shadow: none;
  outline: none;
  border-color: $pj-color-green;

  &:checked {
    background-color: $pj-color-green;
    color: $pj_color-green;
    outline: none;
    border: 0;
    box-shadow: none;
  }
}

#offer-explainer-footer {
  border-radius: 0 0 1rem 1rem;
  background: #f8fbf5;
  position: absolute;
  left: 0;
  right: 0;
  width: 90%;

  & .container {
    & .row {
      margin-top: 0.5rem;
      color: #666;
      font-family: Inter, serif;
      font-size: 0.9375rem;
      font-style: normal;
      font-weight: 500;
      line-height: 1.4375rem;
      letter-spacing: 0.01875rem;
    }
  }
}

#offer-explainer-footer > div:nth-child(n) {
  margin-top: 0.5rem;
  color: #666;
}

#accept-step {
  padding-bottom: 25%;
}
</style>
